import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Sparkles, Star, Crown, Calendar, Gift, Percent } from 'lucide-react';
import siteConfig, { getDiscountedPrice, getPromoDateRange } from '@/config/siteConfig';

const DiscountBadge = ({ discount }) => (
  <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-accent/20 border border-accent/40 hover:bg-accent/30 transition-colors">
    <span className="text-2xl md:text-3xl font-display text-gradient-gold font-bold">-{discount}%</span>
  </div>
);

const PaymentOption = ({ times, discount, originalPrice, label }) => {
  const discountedPrice = getDiscountedPrice(originalPrice, discount);
  const monthlyPayment = times > 1 ? Math.round(discountedPrice / times) : discountedPrice;

  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-card/50 border border-border/30 hover:border-accent/30 transition-colors">
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center text-accent font-body text-sm font-bold">
          {label}
        </span>
        <div>
          <span className="text-foreground font-body text-sm">
            {times === 1 ? 'Paiement unique' : `${times} mensualités`}
          </span>
          <span className="ml-2 px-2 py-0.5 rounded-full bg-accent/20 text-accent text-xs font-bold">
            -{discount}%
          </span>
        </div>
      </div>
      <div className="text-right">
        <span className="text-muted-foreground line-through text-xs mr-2">{originalPrice}€</span>
        <span className="text-gold-luminous font-display text-lg font-bold">{discountedPrice}€</span>
        {times > 1 && (
          <span className="block text-muted-foreground text-xs">soit {monthlyPayment}€/mois</span>
        )}
      </div>
    </div>
  );
};

export const PricingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const { promo } = siteConfig;
  const classicPricing = promo.pricing.classic;
  const vipPricing = promo.pricing.vip;

  const classicFeatures = [
    "Formation complète avec accès illimité",
    "Modules de soins énergétiques",
    "5 fréquences bonus (Baleine, Béluga, Méduse, Hippocampe, Raie)",
    "Live Mensuel",
    "Bonus Dauphins hybrides",
    "Accès communauté active"
  ];

  const vipFeatures = [
    ...classicFeatures,
    "Fil d'urgence",
    "Mentorat et supervision 1to1: 3 RDV d'1H30 avec Gilles"
  ];

  const allDiscounts = [30, 25, 20, 20, 18, 15, 10];

  return (
    <section id="tarifs" ref={ref} className="section-padding bg-background relative overflow-hidden">
      {/* Cosmic Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-cosmic" />
      </div>

      <div className="container-luxury relative">
        {/* Big Golden Promo Banner */}
        {promo.enabled && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="mb-12"
          >
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-accent via-accent-light to-accent p-1">
              <div className="bg-background rounded-[22px] p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-accent/20 flex items-center justify-center">
                      <Gift className="w-8 h-8 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl text-foreground">
                        Offre Spéciale <span className="text-gradient-gold-shine">Limitée</span>
                      </h3>
                      <p className="text-muted-foreground font-body">
                        {getPromoDateRange()}
                      </p>
                    </div>
                  </div>
                  <div className="text-center md:text-right">
                    <Badge className="bg-accent text-background text-xl md:text-2xl px-6 py-2 font-display shadow-gold-intense">
                      JUSQU'À {promo.maxDiscount}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="text-gold-luminous font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Investissement
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight mb-6">
            L'investissement pour rejoindre 
            <span className="text-gradient-gold"> l'école des Maîtres Dauphins</span>
          </h2>
        </motion.div>

        {/* Discount Grid */}
        {promo.enabled && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="mb-12"
          >
            <h3 className="text-center font-display text-xl text-foreground mb-6 flex items-center justify-center gap-2">
              <Percent className="w-5 h-5 text-accent" />
              Grille des réductions selon votre mode de paiement
            </h3>
            <div className="grid grid-cols-4 md:grid-cols-7 gap-3 max-w-3xl mx-auto">
              {allDiscounts.map((discount, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.3 + index * 0.05 }}
                >
                  <DiscountBadge discount={discount} />
                </motion.div>
              ))}
            </div>
            <p className="text-center text-muted-foreground text-sm mt-4 font-body">
              1x • 2x • 3x • 5x • 10x • 12x • 16x
            </p>
          </motion.div>
        )}

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Classique */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            <div className="luxury-card rounded-3xl p-6 md:p-8 h-full border border-border/50">
              <h3 className="font-display text-2xl text-foreground mb-2">Formule Classique</h3>
              <p className="text-muted-foreground font-body mb-4">École des Maîtres Dauphins</p>
              
              {/* Price with promo */}
              <div className="mb-6">
                {promo.enabled && (
                  <Badge className="bg-accent/20 text-accent mb-2">
                    JUSQU'À {promo.maxDiscount}
                  </Badge>
                )}
                <div className="flex items-baseline gap-3 flex-wrap">
                  {promo.enabled && (
                    <span className="text-2xl text-muted-foreground line-through">
                      {classicPricing.originalPrice}€
                    </span>
                  )}
                  <span className="text-4xl md:text-5xl font-display text-gradient-gold whitespace-nowrap">
                    {promo.enabled 
                      ? `${getDiscountedPrice(classicPricing.originalPrice, 30)}€`
                      : `${classicPricing.originalPrice}€`
                    }
                  </span>
                  {promo.enabled && (
                    <span className="text-sm text-accent font-body">en 1x</span>
                  )}
                </div>
              </div>

              {/* Payment options */}
              {promo.enabled && (
                <div className="mb-6 space-y-2">
                  <p className="text-sm text-muted-foreground font-body mb-3">Options de paiement :</p>
                  {classicPricing.payments.map((payment, index) => (
                    <PaymentOption 
                      key={index}
                      {...payment}
                      originalPrice={classicPricing.originalPrice}
                    />
                  ))}
                </div>
              )}

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {classicFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 text-foreground/90 font-body text-sm">
                    <span className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-accent" />
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <a href={siteConfig.mainCTALink} target="_blank" rel="noopener noreferrer" className="block">
                <Button className="w-full btn-outline-gold rounded-full py-6 text-lg">
                  Je m'inscris
                </Button>
              </a>
            </div>
          </motion.div>

          {/* VIP */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="relative"
          >
            {/* Popular badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
              <Badge className="bg-accent text-background font-body px-4 py-1 shadow-gold">
                <Star className="w-4 h-4 mr-1 fill-current" />
                Populaire
              </Badge>
            </div>

            <div className="luxury-card-gold rounded-3xl p-6 md:p-8 h-full border-2 border-accent/50 shadow-gold-intense">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-6 h-6 text-accent" />
                <h3 className="font-display text-2xl text-foreground">Formule VIP</h3>
              </div>
              <p className="text-muted-foreground font-body mb-4">
                École des Maîtres + Mentorat 1to1 Dauphins
              </p>
              
              {/* Price with promo */}
              <div className="mb-6">
                {promo.enabled && (
                  <Badge className="bg-accent/30 text-accent mb-2">
                    JUSQU'À {promo.maxDiscount}
                  </Badge>
                )}
                <div className="flex items-baseline gap-3 flex-wrap">
                  {promo.enabled && (
                    <span className="text-2xl text-muted-foreground line-through">
                      {vipPricing.originalPrice}€
                    </span>
                  )}
                  <span className="text-4xl md:text-5xl font-display text-gradient-gold-shine whitespace-nowrap">
                    {promo.enabled 
                      ? `${getDiscountedPrice(vipPricing.originalPrice, 30)}€`
                      : `${vipPricing.originalPrice}€`
                    }
                  </span>
                  {promo.enabled && (
                    <span className="text-sm text-accent font-body">en 1x</span>
                  )}
                </div>
              </div>

              {/* Payment options for VIP */}
              {promo.enabled && (
                <div className="mb-6 space-y-2">
                  <p className="text-sm text-muted-foreground font-body mb-3">Options de paiement :</p>
                  {vipPricing.payments.map((payment, index) => (
                    <PaymentOption 
                      key={index}
                      {...payment}
                      originalPrice={vipPricing.originalPrice}
                    />
                  ))}
                </div>
              )}

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {vipFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 text-foreground/90 font-body text-sm">
                    <span className="w-5 h-5 rounded-full bg-accent/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-accent" />
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <a href={siteConfig.mainCTALink} target="_blank" rel="noopener noreferrer" className="block">
                <Button className="w-full btn-gold rounded-full py-6 text-lg">
                  Je choisis le cursus VIP
                </Button>
              </a>
            </div>
          </motion.div>
        </div>

        {/* RDV Clarté Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="mt-12 max-w-3xl mx-auto"
        >
          <div className="glass-gold rounded-3xl p-6 md:p-8 text-center shadow-gold">
            <Calendar className="w-10 h-10 text-accent mx-auto mb-4" />
            <h3 className="font-display text-xl text-foreground mb-3">
              Tu hésites ? Réserve un <span className="text-gradient-gold">RDV Clarté</span> gratuit
            </h3>
            <p className="text-muted-foreground font-body text-sm mb-4">
              Un appel de 20 minutes avec Celia pour répondre à toutes tes questions.
            </p>
            <a href={siteConfig.rdvClarityLink} target="_blank" rel="noopener noreferrer">
              <Button className="btn-outline-gold rounded-full px-6 py-3">
                <Calendar className="w-4 h-4 mr-2" />
                Prendre RDV avec Celia
              </Button>
            </a>
          </div>
        </motion.div>

        {/* Guarantee Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-10 max-w-4xl mx-auto"
        >
          <div className="glass-card rounded-3xl p-6 md:p-10 text-center">
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2024/03/satisfait-1024x1024.png"
              alt="Garantie satisfait ou remboursé"
              className="w-24 h-24 mx-auto mb-4 object-contain"
            />
            <h3 className="font-display text-xl text-foreground mb-3">
              <span className="text-gradient-gold">0 risques</span> avec la garantie "satisfait·e ou remboursé·e"
            </h3>
            <p className="text-muted-foreground font-body text-sm leading-relaxed">
              Tu as 14 jours pour faire le test et découvrir le module 1. Si cela ne correspond pas à tes attentes : remboursement intégral.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
